// Center page (SPEC §7.7): details and closures; adding a closure lists the lessons that won't take place.

import type { ApiResult } from "../../api/client";
import { addClosure, fetchCenter, fetchCenters, removeClosure } from "../../api/centers";
import type { Center, ClosureReason, ClosureResult, Lesson } from "../../api/types";
import { formatDate, t, translateCode } from "../../i18n/i18n";
import { setRedraw, type RouteMatch } from "../../router";
import { isAdmin, requireSchool } from "../../session";
import { createButton, createElement, createLink } from "../../ui/dom";
import { createErrorBox, showProblem } from "../../ui/feedback";
import { createCheckbox, createInputField, createSelectField, showFieldErrors, type Field } from "../../ui/forms";
import { lessonRoute } from "../../ui/lessonLabels";
import { reportAndReload } from "../../ui/resultMessage";
import { formatRange } from "../../time";
import { createLoading, showPageProblem, startPage } from "../pageFrame";

interface CenterPageState {
  center: Center;
  otherCenterIds: string[];
  notTakingPlace: Lesson[] | null;
}

export async function renderCenterPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const page: HTMLElement = startPage(container, t("admin.centers"));
  page.append(createLoading());
  const center: ApiResult<Center> = await fetchCenter(route.params.id ?? "");
  const centers: ApiResult<Center[]> = await fetchCenters(false);
  if (center.ok === false) {
    showPageProblem(page, center.problem);
    return;
  }

  const others: string[] = centers.ok ? centers.data.map((item: Center): string => item.id)
    .filter((id: string): boolean => id !== center.data.id) : [];
  const state: CenterPageState = { center: center.data, otherCenterIds: others, notTakingPlace: null };
  const draw = (): void => {
    drawPage(container, state);
  };
  setRedraw(container, draw);
  draw();
}

function drawPage(container: HTMLElement, state: CenterPageState): void {
  const page: HTMLElement = startPage(container, state.center.name);
  page.append(createElement("p", "muted", [state.center.address, state.center.note].filter(Boolean).join(" · ")));
  page.append(createElement("h2", "", t("admin.closures")), buildClosuresTable(state.center));
  if (isAdmin()) {
    page.append(buildAddClosureForm(container, state));
  }

  if (state.notTakingPlace !== null) {
    page.append(buildNotTakingPlace(state.notTakingPlace));
  }
}

function buildClosuresTable(center: Center): HTMLElement {
  if (center.closures.length === 0) {
    return createElement("p", "muted", t("admin.noClosures"));
  }

  const table: HTMLTableElement = createElement("table");
  const head: HTMLTableRowElement = table.createTHead().insertRow();
  for (const title of [t("app.from"), t("app.to"), t("app.reason"), t("app.note"), ""]) {
    head.append(createElement("th", "", title));
  }

  const body: HTMLTableSectionElement = table.createTBody();
  for (const closure of center.closures) {
    const row: HTMLTableRowElement = body.insertRow();
    for (const text of [formatDate(closure.dateFrom), formatDate(closure.dateTo),
      translateCode("closureReasons", closure.reason), closure.note ?? ""]) {
      row.append(createElement("td", "", text));
    }

    const action: HTMLTableCellElement = row.insertCell();
    if (isAdmin()) {
      action.append(createButton(t("app.remove"), async (): Promise<void> => {
        await reportAndReload(await removeClosure(center.id, closure.id));
      }, "button-link"));
    }
  }

  return table;
}

function buildAddClosureForm(container: HTMLElement, state: CenterPageState): HTMLElement {
  const today: string = requireSchool().today;
  const from: Field<HTMLInputElement> = createInputField(t("app.from"), "date", today);
  const to: Field<HTMLInputElement> = createInputField(t("app.to"), "date", today);
  const reasons: { value: string; label: string }[] = ["Holiday", "Other"].map(
    (code: string): { value: string; label: string } => ({
      value: code,
      label: translateCode("closureReasons", code),
    }));
  const reason: Field<HTMLSelectElement> = createSelectField(t("app.reason"), reasons, "Holiday");
  const note: Field<HTMLInputElement> = createInputField(t("app.note"), "text", "");
  const alsoAll: Field<HTMLInputElement> = createCheckbox(t("admin.alsoAllCenters"), false);
  const errorBox: HTMLDivElement = createErrorBox();
  const save: HTMLButtonElement = createButton(t("admin.addClosure"), async (): Promise<void> => {
    const result: ApiResult<ClosureResult> = await addClosure(state.center.id, {
      dateFrom: from.input.value, dateTo: to.input.value, reason: reason.input.value as ClosureReason,
      note: note.input.value, alsoForCenterIds: alsoAll.input.checked ? state.otherCenterIds : [],
    });
    showProblem(errorBox, result.ok ? null : result.problem);
    showFieldErrors(result.ok ? null : result.problem, { dateFrom: from, dateTo: to, reason, note });
    if (result.ok) {
      await refreshCenter(container, state, result.data.lessonsNotTakingPlace);
    }
  }, "button-primary");
  const grid: HTMLDivElement = createElement("div", "form-grid");
  grid.append(from.wrapper, to.wrapper, reason.wrapper, note.wrapper);
  const card: HTMLDivElement = createElement("div", "card");
  card.append(createElement("h3", "", t("admin.addClosure")), grid, alsoAll.wrapper, errorBox, save);
  return card;
}

async function refreshCenter(container: HTMLElement, state: CenterPageState, lessons: Lesson[]): Promise<void> {
  const center: ApiResult<Center> = await fetchCenter(state.center.id);
  if (center.ok) {
    state.center = center.data;
  }

  state.notTakingPlace = lessons;
  drawPage(container, state);
}

/** The lessons that will not take place, grouped by date (SPEC §7.7). */
function buildNotTakingPlace(lessons: Lesson[]): HTMLElement {
  const box: HTMLDivElement = createElement("div", "banner");
  box.append(createElement("p", "", t("admin.lessonsNotTakingPlace")));
  let currentDate: string = "";
  let list: HTMLUListElement = createElement("ul");
  for (const lesson of lessons) {
    if (lesson.key.date !== currentDate) {
      currentDate = lesson.key.date;
      list = createElement("ul");
      box.append(createElement("strong", "", formatDate(currentDate)), list);
    }

    const item: HTMLLIElement = createElement("li");
    item.append(createLink(lessonRoute(lesson), `${formatRange(lesson.range)} ${lesson.groupName} — ${
      lesson.centerName}`));
    list.append(item);
  }

  return box;
}
